import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { PaginatedServiceHistories } from '../api/types';
import config from '../config';
import { Duration } from '../constants';

async function getServiceHistory(props: {
  axios: AxiosInstance;
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: string;
  workshopId?: number;
  make?: string;
  language?: string;
  systemPartner?: string;
  country?: string;
}): Promise<PaginatedServiceHistories> {
  const { axios, searchString, page, itemsPerPage, status = [], assignee, workshopId, make, language, systemPartner, country } = props;

  const { data } = await axios.get(`${config.apiBaseUrl}/op/v2/service-history-request`, {
    params: {
      searchString: searchString || '',
      page,
      itemsPerPage,
      status: status.length > 0 ? status?.join() : undefined,
      make_id: make,
      language,
      workshop_id: workshopId,
      user_id: assignee,
      systempartner: systemPartner,
      country,
    },
  });
  return data;
}

export default function useServiceHistoriesQuery({
  searchString,
  page,
  itemsPerPage,
  status,
  assignee,
  workshopId,
  make,
  language,
  systemPartner,
  country,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: string;
  workshopId?: number;
  make?: string;
  language?: string;
  systemPartner?: string;
  country?: string;
}) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({
      searchString,
      page,
      itemsPerPage,
      status,
      assignee,
      workshopId,
      make,
      language,
      systemPartner,
      country,
    }),
    queryFn: () =>
      getServiceHistory({
        axios,
        searchString,
        page,
        itemsPerPage,
        status,
        assignee,
        workshopId,
        make,
        language,
        systemPartner,
        country,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
    staleTime: Duration.Minute,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

function getKey({
  searchString,
  page,
  itemsPerPage,
  status,
  assignee,
  workshopId,
  make,
  language,
  systemPartner,
  country,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  assignee?: string;
  workshopId?: number;
  make?: string;
  language?: string;
  systemPartner?: string;
  country?: string;
}) {
  return [
    'ServiceHistories',
    searchString,
    itemsPerPage,
    page,
    status,
    assignee,
    workshopId,
    make,
    language,
    systemPartner,
    country,
  ] as const satisfies QueryKey;
}

useServiceHistoriesQuery.getKey = getKey;
